import React, { useState, useEffect } from "react";
import { API_URL } from "./services/api";
import { TbPlus } from "react-icons/tb";
import { FiLogOut } from "react-icons/fi";
import { BiFolderOpen } from "react-icons/bi";
import Modal from "./components/base/Modal"
import { HiOutlineExclamationCircle } from "react-icons/hi";


const ProjectsDemo = ({ onProjectAccess, userIdToken, handleStorageChange }) => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  // Fetch Projects
  const fetchProjects = async () => {
    setLoading(true);
    setError("");

    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/userProjects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include"
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || "Login failed.");
      }
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (err) {
      console.error("Project Error:", err)
      setError("Unable to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  // Create Project
  const handleCreateProject = async () => {
    setError("");
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectCreate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          project_name: newProjectName,
          //   project_id: newProjectId,
        }),
        credentials: "include"
      });
      if (!response.ok) {
        throw new Error(`Failed to create project! Status: ${response.status}`);
      }
      setIsModalOpen(false);
      setNewProjectName("");
      //   setNewProjectId("");
      fetchProjects(); // Refresh projects after successful creation
    } catch (err) {
      setError("Failed to create project. Please try again.");
    }
  };

  // Delete Project
  const handleDeleteProject = async (projectId) => {
    setError("");
    setDeleteLoading(true);
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectDelete/${projectId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include"
      });
      if (!response.ok) {
        throw new Error(`Failed to delete project! Status: ${response.status}`);
      }
      fetchProjects(); // Refresh projects after successful deletion
    } catch (err) {
      setError("Failed to delete project. Please try again.");
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Delete Project
  const handleAccessProject = async (projectId) => {
    setError("");
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectLogin/${projectId}`, {
        // method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include"
      });
      if (!response.ok) {
        throw new Error(`Failed to delete project! Status: ${response.status}`);
      }
      const data = await response.json();
      onProjectAccess(data.token)

      //   fetchProjects(); // Refresh projects after successful deletion
    } catch (err) {
      setError("Failed to delete project. Please try again.");
    }
  };

  const handleLogout = () => {
    async function logout() {
      try {
        const response = await fetch(`${API_URL}/logout`, {
          method: 'POST',
          credentials: 'include',  // 🔥 important! send cookies with the request
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok) {
          console.log('Logout successful:', data.message);
        } else {
          console.error('Logout failed:', data.detail || 'Unknown error');
        }
      } catch (error) {
        console.error('Network error during logout:', error);
      } finally {
        localStorage.clear();
        handleStorageChange();
      }
    }
    logout();

  }

  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br bg-1 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-zinc-900 shadow-lg rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-zinc-200">Projects</h1>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 bg-zinc-700 text-white font-bold rounded-lg hover:bg-zinc-600 transition"
          >
            <FiLogOut className="mr-2" size={20} /> Log Out
          </button>
        </div>
        {loading && <p className="text-center text-zinc-400">Loading...</p>}
        {error && <div className="text-red-500 text-center mb-4">{error}</div>}
        {projects.length === 0 && !loading && (
          <p className="text-center text-zinc-400">No projects found. Create one!</p>
        )}
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-gradient-to-b from-[#5566FF] to-[#333D99] text-white font-bold rounded-lg hover:opacity-90 transition mb-6"
        >
          <TbPlus className="mr-2" size={20} /> Create Project
        </button>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.project_id}
              className="p-4 bg-zinc-700 text-white rounded-lg shadow transform transition hover:scale-105"
            >
              <h2 className="text-xl font-semibold">{project.project_name}</h2>
              <p className="text-sm text-zinc-400">ID: {project.project_id}</p>
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => handleAccessProject(project.project_id)}
                  className="flex items-center px-4 py-2 bg-gradient-to-b from-[#5566FF] to-[#333D99] text-white font-bold rounded-lg hover:opacity-90 transition"
                >
                  <BiFolderOpen className="mr-2" size={20} /> Access Project
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <Modal isOpen={true} onClose={() => setIsModalOpen(false)} title={"Create New Project"}>
          <div className="p-8">
            <input
              type="text"
              placeholder="Project Name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition duration-200"
              required
            />

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={handleCreateProject}
                className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 
                border border-zinc-700 rounded-md transition duration-200
                focus:outline-none focus:ring-2 focus:ring-zinc-500 flex flex-direction-row items-center"
              >
                <TbPlus className="mr-2" size={20} /> Create
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Project Warning modal */}
      {isDeleteModalOpen && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-zinc-800 text-white rounded-xl shadow-lg max-w-md w-full p-6 text-sm md:text-base">
            <div className="flex items-center mb-4">
              <HiOutlineExclamationCircle className="text-yellow-400 mr-3" size={24} />
              <h2 className="text-lg md:text-xl font-bold">Delete Project</h2>
            </div>
            <p className="mb-6 text-zinc-300">
              Do you really want to delete <span className="font-semibold text-white">"{selectedProject.project_name}"</span> project?
            </p>
            <div className="flex justify-end gap-3 md:gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-3 py-1.5 md:px-4 md:py-2 font-semibold rounded-lg bg-zinc-700 text-white hover:bg-zinc-600 border border-zinc-600 transition text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                Disabled={deleteLoading}
                onClick={() => {
                  handleDeleteProject(selectedProject.project_id);

                }}
                className={` px-3 py-1.5 md:px-4 md:py-2 font-semibold rounded-lg bg-red-600 text-white hover:bg-red-500 transition text-sm md:text-base ${deleteLoading && "animate-pulse"} `}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsDemo;